import { useState, useEffect } from 'react';

interface SidebarGroup {
  label: string | null;
  items: { id: string; label: string }[];
}

const GROUPS: SidebarGroup[] = [
  {
    label: null,
    items: [
      { id: 'overview', label: 'Overview' },
      { id: 'how-it-works', label: 'How It Works' },
      { id: 'authentication', label: 'Authentication' },
    ],
  },
  {
    label: 'Core API',
    items: [
      { id: 'health', label: 'Health Check' },
      { id: 'register', label: 'Register' },
      { id: 'merchant-profile', label: 'Merchant Profile' },
      { id: 'create-checkout', label: 'Create Checkout' },
      { id: 'get-order', label: 'Get Order' },
      { id: 'order-status', label: 'Order Status' },
      { id: 'verify', label: 'Verify Payment' },
      { id: 'webhooks', label: 'Webhooks' },
    ],
  },
  {
    label: 'Currency',
    items: [
      { id: 'convert', label: 'Convert' },
      { id: 'spot', label: 'Spot Rates' },
      { id: 'currencies', label: 'Currencies' },
      { id: 'candle', label: 'Candle' },
      { id: 'candles', label: 'Candles' },
      { id: 'historical', label: 'Historical' },
      { id: 'forward', label: 'Forward' },
      { id: 'quota', label: 'Quota' },
      { id: 'datasets', label: 'Datasets' },
    ],
  },
  {
    label: 'Reference',
    items: [
      { id: 'integration', label: 'Integration Guide' },
      { id: 'errors', label: 'Errors' },
      { id: 'rate-limits', label: 'Rate Limits' },
      { id: 'settlement', label: 'Settlement' },
    ],
  },
];

const ALL_IDS = GROUPS.flatMap((g) => g.items.map((i) => i.id));

export default function DocsSidebar() {
  const [activeId, setActiveId] = useState('overview');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    ALL_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <nav className="space-y-6">
      {GROUPS.map((group, gi) => (
        <div key={gi}>
          {group.label && (
            <p className="font-mono text-[9px] text-bone-white/25 uppercase tracking-widest mb-2 mt-2">
              {group.label}
            </p>
          )}
          <div className="space-y-0.5">
            {group.items.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`block w-full text-left py-1.5 pl-3 border-l transition-colors ${
                  activeId === id
                    ? 'text-bone-white border-bone-white/40'
                    : 'text-bone-white/30 border-transparent hover:text-bone-white/60 hover:border-bone-white/10'
                } font-mono text-[11px] tracking-wider`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}
